import type { ComponentProps } from 'react';
import React from 'react';
import type { PartialBy } from 'corgi-types';

import ClearAllFiltersButton from 'common/components/ClearAllFiltersButton';
import { Flex } from 'common/components/Flex';
import { HeadlineH4, SubtitleST3 } from 'common/components/Typography';
import { useLocalization } from 'common/providers/LocaleProvider';
import type { FiltersParams } from 'common/types/SortAndFilter';
import { getAllSelectedFilters } from '../../category-selected-filters/components/utils';
import { getConstructorAttributes } from '../utils';

interface ProductListEmptyProps extends PartialBy<FiltersParams, 'filters'> {
  browsedByValue?: string;
  browsedBy?: string;
  isSearchMode: boolean;
  resultId?: string;
}

const headlineStyles: ComponentProps<typeof HeadlineH4> = {
  mt: 'sp_0',
  mb: ['sp_4', null, null, 'sp_8'],
};

const styledTextStyles: ComponentProps<typeof SubtitleST3> = {
  color: 'sys.neutral.border.hover',
  mt: 'sp_0',
  mb: ['sp_16', null, 'sp_20', 'sp_24'],
  px: ['sp_16', 'sp_24'],
  textAlign: 'center',
};

const containerStyles: ComponentProps<typeof Flex> = {
  flexDirection: 'column',
  alignItems: 'center',
  py: 'sp_48',
};

const ProductListEmpty = ({
  browsedByValue,
  browsedBy,
  isSearchMode,
  resultId,
  filters = [],
}: ProductListEmptyProps) => {
  const { t } = useLocalization();
  const text =
    getAllSelectedFilters(filters).length > 0
      ? 'h24_nothing_found_with_filters_text'
      : 'h24_nothing_found_without_filters_text';

  const attributes = getConstructorAttributes({
    isSearchMode,
    totalCount: 0,
    browsedByValue,
    resultId,
    browsedBy,
  });

  return (
    <Flex data-testid="empty-list" {...containerStyles} {...attributes}>
      <HeadlineH4 data-testid="empty-list-headline" {...headlineStyles}>
        {t('h24_nothing_found_headline')}
      </HeadlineH4>
      <SubtitleST3 data-testid="empty-list-text" {...styledTextStyles}>
        {t(text)}
      </SubtitleST3>
      <ClearAllFiltersButton filters={filters} withBorder={true} />
    </Flex>
  );
};

export default ProductListEmpty;
